import { Button, Card, ConfirmModal } from '@/components/Bicester';
import React from 'react';
import ServiceInfo from '../../components/ServiceInfo';
import { fetchAddEditLease, fetchGiveBack, fetchLeaseInfo } from '@/services/site/lease';
import { defaultLeaseEditInfo, leaseSchema } from '../../Lease/constants';
import { getParamsWithNilToString } from '@/services/utils';
import { BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import useLeaseBasic from '@/hooks/preload/useLeaseBasic';
import { FieldArray } from 'formik';
import { IconButton } from 'rsuite5';
import { Alert } from 'rsuite';
import Image from '@/components/Bicester/Image';
import { useRequest } from 'ahooks';
import { useModal } from '@ebay/nice-modal-react';
import { SERVICE_STATUS_TYPE } from '@/constants';
import { SERVICE_STATUS } from '@/services/constants';

const LeaseInfo = () => {
  const { data: leaseBasic } = useLeaseBasic();
  const modal = useModal(ConfirmModal);

  const { run: giveBack } = useRequest(fetchGiveBack, {
    manual: true,
  });

  return (
    <ServiceInfo
      defaultServiceInfo={defaultLeaseEditInfo}
      serviceSchema={leaseSchema}
      fetchInfo={fetchLeaseInfo}
      fetchEdit={fetchAddEditLease}
      getDefaultInfo={(data) => {
        return {
          is_subscribe: data?.is_subscribe,
          servicesite_id: data?.servicesite_id,
          subscribe_date: data?.subscribe_date,
          subscribe_style: data?.subscribe_style,
          subscribe_remark: data?.subscribe_remark,
          start_date: data?.start_date,
          end_date: data?.end_date,
          pledge_type: data?.pledge_type,
          pledge_price: data?.pledge_price,
          pos_no: data?.pos_no,
          remark: data?.remark,
          lease_status: data?.lease_status,
          lease_items: data?.lease_items,
        };
      }}
      getSubmitData={(id, values) =>
        Object.assign({}, getParamsWithNilToString(values), {
          lease_items: JSON.stringify(
            values.lease_items?.map((item) => {
              return {
                item_type: item?.item_type,
                item_amount: item?.item_amount,
              };
            })
          ),
          id,
        })
      }
      serviceInfo={({ formik, infoData, isEdit }) => (
        <>
          <Card.Picker
            notEditable
            label="本次服务是否预约"
            formKey="is_subscribe"
            pickerType="TorF"
          />
          <Card.DatePicker notEditable label="预约日期" formKey="subscribe_date" />
          <Card.Input notEditable label="预约方式" formKey="subscribe_style" />
          <Card.Input notEditable label="预约备注" formKey="subscribe_remark" />
          <Card.DatePicker
            required
            isEdit={isEdit}
            label="租赁开始时间"
            formKey="start_date"
            format={BICESTER_FULL_DATE_HOURS_MINUTES}
            readOnlyFormat={BICESTER_FULL_DATE_HOURS_MINUTES}
          />
          <Card.DatePicker
            isEdit={isEdit}
            label="租赁结束时间"
            formKey="end_date"
            format={BICESTER_FULL_DATE_HOURS_MINUTES}
            readOnlyFormat={BICESTER_FULL_DATE_HOURS_MINUTES}
          />
          <Card.Input
            notEditable
            label="服务编号"
            formKey="service_no"
            value={infoData?.service_no}
          />
          <Card.Input notEditable label="服务大使" formKey="ge_name" value={infoData?.ge_name} />
          <Card.Picker
            required
            isEdit={isEdit}
            label="质押方式"
            formKey="pledge_type"
            pickerData={leaseBasic.pledge_type}
            value={infoData?.pledge_type_name}
            searchable
          />
          <Card.Input
            isEdit={isEdit}
            label="质押金额"
            formKey="pledge_price"
            inputType="price"
            value={0}
          />
          <Card.Input isEdit={isEdit} label="POS小票编号" formKey="pos_no" />
          <Card.Input isEdit={isEdit} label="备注" formKey="remark" inputType="textarea" />
          <Card.Picker
            isEdit={isEdit}
            label="是否归还物品"
            formKey="lease_status"
            pickerData={[
              { label: '未归还', value: 1 },
              { label: '已归还', value: 2 },
            ]}
          />
        </>
      )}
      thirdCardInfo={({ formik, infoData, isEdit }) => (
        <Card title="租赁物品情况" className="flex-[auto]">
          <FieldArray name="lease_items">
            {({ insert, remove, push }) => (
              <>
                {formik.values.lease_items.length > 0 &&
                  formik.values.lease_items.map((item, index) => (
                    <div className="flex" key={index}>
                      <Card.Picker
                        required
                        isEdit={isEdit}
                        className="flex-[3]"
                        label="租赁物品"
                        formKey={`lease_items.${index}.item_type`}
                        pickerData={leaseBasic.item_type}
                        disabledItemValues={
                          formik.values?.lease_items?.map((item) => item.item_type) ?? []
                        }
                        value={infoData?.lease_items[index]?.item_type_name}
                        searchable
                        labelWeight={5}
                      />
                      <div className="flex-1 flex items-center">
                        <div className="text-[#999999] ml-[20px]">数量</div>
                        <Card.Input
                          isEdit={isEdit}
                          className="ml-[10px]"
                          inputStyle={{ textAlign: 'center', padding: 0 }}
                          inputType="number"
                          formKey={`lease_items.${index}.item_amount`}
                        />
                        {isEdit && (
                          <IconButton
                            className={`w-[17px] h-[17px] ml-[5px] ${
                              index === 0 && formik.values.lease_items.length <= 1
                                ? 'invisible'
                                : ''
                            }`}
                            onClick={() => remove(index)}
                          >
                            <Image src="/resources/images/delete.svg" />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  ))}
                {isEdit && (
                  <div className="flex">
                    <div className="flex-[3]"></div>
                    <div className="flex-[5]">
                      <IconButton
                        className="flex items-center"
                        onClick={() => push({ item_type: null, item_amount: '1' })}
                      >
                        <Image src="/resources/images/add.svg" />
                        <div className="text-[12px] text-[#999999] ml-[5px]">新增</div>
                      </IconButton>
                    </div>
                  </div>
                )}
              </>
            )}
          </FieldArray>
        </Card>
      )}
      infoBottomToolbar={({ service_id, infoData, isEdit, onReload }) => (
        <div className="flex justify-end mt-[36px]">
          <Button
            className="rs-btn-primary w-[238px] py-[6px] mr-[30px]"
            appearance="link"
            href={`/api/service/leasePrint?service_id=${service_id}`}
            target="_blank"
            disabled={isEdit} // 编辑状态无法点击
          >
            打印该客户租赁凭证
          </Button>
          <Button
            className="w-[238px] py-[6px]"
            disabled={
              isEdit ||
              infoData?.status === SERVICE_STATUS_TYPE.CANCELED ||
              infoData?.lease_status === 2
            } // 编辑状态或已取消或已归还的服务无法点击
            onClick={() => {
              modal.show({
                contentText: '是否确认一键归还？',
                submitText: '确定',
                cancelText: '放弃',
                onSubmit: () => {
                  giveBack(service_id).then((res) => {
                    if (res?.status === SERVICE_STATUS.SUCCESS) {
                      Alert.success('归还成功');
                      onReload();
                    }
                  });
                },
              });
            }}
          >
            一键归还
          </Button>
        </div>
      )}
    />
  );
};

export default LeaseInfo;
